'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';

const zones = [
  {
    phase: '01',
    label: 'Awareness',
    headline: 'The body speaks before the mind listens.',
    body: 'Every symptom is a signal. Every pattern has a root. Naturopathy begins where conventional medicine stops — at the origin.',
  },
  {
    phase: '02',
    label: 'Understanding',
    headline: 'Healing is not suppression. It is resolution.',
    body: 'When you understand what your body is communicating, the path forward becomes clear — not complicated.',
  },
  {
    phase: '03',
    label: 'Transformation',
    headline: 'Lasting change happens in layers.',
    body: 'Physical. Emotional. Energetic. Each dimension must be addressed for transformation to hold.',
  },
];

const TOTAL_STAGES = zones.length;
const STAGE_THRESHOLD = 300;

export default function ScrollSequenceScene() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeZone, setActiveZone] = useState(0);
  const [progress, setProgress] = useState(0);
  const [locked, setLocked] = useState(false);

  const accDelta = useRef(0);
  const lockedRef = useRef(false);
  const activeZoneRef = useRef(0);
  // Track whether we've exited forward (completed all stages)
  const completedRef = useRef(false);

  useEffect(() => { lockedRef.current = locked; }, [locked]);
  useEffect(() => { activeZoneRef.current = activeZone; }, [activeZone]);

  const computeProgress = useCallback((zone: number, delta: number) => {
    const stageProgress = Math.min(1, Math.max(0, delta / STAGE_THRESHOLD));
    return (zone + stageProgress) / TOTAL_STAGES;
  }, []);

  const enterLock = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    sectionRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
  }, []);

  const exitLock = useCallback((direction: 'forward' | 'backward') => {
    lockedRef.current = false;
    setLocked(false);
    accDelta.current = 0;
    if (direction === 'forward') {
      completedRef.current = true;
    }
  }, []);

  // IntersectionObserver — re-lock whenever section enters viewport
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // Always re-lock when entering, reset completed state
          completedRef.current = false;
          accDelta.current = 0;
          enterLock();
        } else if (!entry.isIntersecting) {
          if (lockedRef.current) exitLock('backward');
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [enterLock, exitLock]);

  // Wheel handler — attached to WINDOW when locked to capture all scroll events
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!lockedRef.current) return;
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const currentZone = activeZoneRef.current;

      if (delta > 0) {
        accDelta.current += delta;
        if (accDelta.current >= STAGE_THRESHOLD) {
          accDelta.current = 0;
          if (currentZone < TOTAL_STAGES - 1) {
            const next = currentZone + 1;
            setActiveZone(next);
            setProgress(computeProgress(next, 0));
          } else {
            setProgress(1);
            exitLock('forward');
          }
        } else {
          setProgress(computeProgress(currentZone, accDelta.current));
        }
      } else if (delta < 0) {
        accDelta.current += delta;
        if (accDelta.current <= -STAGE_THRESHOLD) {
          accDelta.current = 0;
          if (currentZone > 0) {
            const prev = currentZone - 1;
            setActiveZone(prev);
            setProgress(computeProgress(prev, 0));
          } else {
            exitLock('backward');
          }
        } else {
          const clampedDelta = Math.max(0, accDelta.current);
          setProgress(computeProgress(currentZone, clampedDelta));
        }
      }
    },
    [computeProgress, exitLock]
  );

  const touchStartY = useRef(0);
  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!lockedRef.current) return;
      e.preventDefault();
      const dy = touchStartY.current - e.touches[0].clientY;
      touchStartY.current = e.touches[0].clientY;

      const currentZone = activeZoneRef.current;
      if (dy > 0) {
        accDelta.current += dy * 3;
        if (accDelta.current >= STAGE_THRESHOLD) {
          accDelta.current = 0;
          if (currentZone < TOTAL_STAGES - 1) {
            const next = currentZone + 1;
            setActiveZone(next);
            setProgress(computeProgress(next, 0));
          } else {
            setProgress(1);
            exitLock('forward');
          }
        } else {
          setProgress(computeProgress(currentZone, accDelta.current));
        }
      } else if (dy < 0) {
        accDelta.current += dy * 3;
        if (accDelta.current <= -STAGE_THRESHOLD) {
          accDelta.current = 0;
          if (currentZone > 0) {
            const prev = currentZone - 1;
            setActiveZone(prev);
            setProgress(computeProgress(prev, 0));
          } else {
            exitLock('backward');
          }
        } else {
          const clampedDelta = Math.max(0, accDelta.current);
          setProgress(computeProgress(currentZone, clampedDelta));
        }
      }
    },
    [computeProgress, exitLock]
  );

  // Attach wheel/touch to WINDOW when locked — this ensures we capture all scroll
  useEffect(() => {
    if (!locked) return;

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [locked, handleWheel, handleTouchStart, handleTouchMove]);

  // Lock body scroll when locked
  useEffect(() => {
    if (locked) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [locked]);

  return (
    <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:212:4" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="212" data-component-end-line="489" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      ref={sectionRef}
      style={{
        position: 'relative',
        height: '100vh',
        background: '#F4EFE6',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Ambient orb */}
      <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:224:6" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="224" data-component-end-line="230" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20pointer-events-none%22%7D"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 60% 50% at 50% ${45 + progress * 10}%, rgba(26,107,107,${0.06 + progress * 0.07}) 0%, transparent 65%)`,
          transition: 'background 0.4s ease',
        }}
      />

      {/* Grain texture */}
      <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:233:6" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="233" data-component-end-line="239" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20pointer-events-none%20opacity-%5B0.018%5D%22%7D"
        className="absolute inset-0 pointer-events-none opacity-[0.018]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }}
      />

      {/* Progress line — left edge */}
      <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:242:6" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="242" data-component-end-line="254" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20left-0%20top-0%20w-px%20pointer-events-none%22%7D"
        className="absolute left-0 top-0 w-px pointer-events-none"
        style={{ height: '100%', background: 'rgba(26,107,107,0.1)' }}
      >
        <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:246:8" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="246" data-component-end-line="253" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          style={{
            width: '100%',
            height: `${progress * 100}%`,
            background: 'rgba(26,107,107,0.4)',
            transition: 'height 0.08s linear',
          }}
        />
      </div>

      {/* Main layout — vertically centered */}
      <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:257:6" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="257" data-component-end-line="488" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20flex%20flex-col%20flex-1%20items-center%20justify-center%20px-6%22%7D" className="relative z-10 flex flex-col flex-1 items-center justify-center px-6">

        {/* ── Step progress display ── */}
        <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:260:8" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="260" data-component-end-line="369" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mb-10%20flex-shrink-0%20flex%20flex-col%20items-center%22%7D"
          className="relative mb-10 flex-shrink-0 flex flex-col items-center"
          style={{ width: 'clamp(260px, 36vw, 420px)' }}
        >
          {/* Stage number + label */}
          <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:265:10" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="265" data-component-end-line="287" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20mb-6%22%7D" className="flex flex-col items-center mb-6">
            <span data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:266:12" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="266" data-component-end-line="276" data-component-file="ScrollSequenceScene.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-serif%22%7D"
              className="font-serif"
              style={{
                fontSize: 'clamp(2.8rem, 5vw, 4.2rem)',
                color: 'rgba(36,44,44,0.75)',
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              {String(activeZone + 1).padStart(2, '0')}
            </span>
            <span data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:277:12" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="277" data-component-end-line="286" data-component-file="ScrollSequenceScene.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-sans%20uppercase%20mt-2%22%2C%22textContent%22%3A%22of%2003%22%7D"
              className="font-sans uppercase mt-2"
              style={{
                fontSize: '0.6rem',
                color: 'rgba(26,107,107,0.7)',
                letterSpacing: '0.22em',
              }}
            >
              of 03
            </span>
          </div>

          {/* Linear progress track */}
          <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:290:10" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="290" data-component-end-line="368" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20flex%20flex-col%20gap-3%22%7D" className="w-full flex flex-col gap-3">
            {/* Track bar */}
            <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:292:12" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="292" data-component-end-line="339" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              style={{
                width: '100%',
                height: '2px',
                background: 'rgba(26,107,107,0.15)',
                borderRadius: '2px',
                position: 'relative',
              }}
            >
              <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:301:14" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="301" data-component-end-line="312" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${progress * 100}%`,
                  background: 'rgba(26,107,107,0.6)',
                  borderRadius: '2px',
                  transition: 'width 0.12s linear',
                }}
              />
              {/* Step dots on track */}
              {[0, 1, 2].map((i) => {
                const isPast = i < activeZone;
                const isActive = i === activeZone;
                return (
                  <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:318:18" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="318" data-component-end-line="336" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                    key={i}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: `${(i / 2) * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: isActive ? '8px' : '6px',
                      height: isActive ? '8px' : '6px',
                      borderRadius: '50%',
                      background: isActive
                        ? 'rgba(26,107,107,0.95)'
                        : isPast
                        ? 'rgba(26,107,107,0.55)'
                        : 'rgba(26,107,107,0.2)',
                      border: isActive ? '1.5px solid rgba(26,107,107,0.4)' : 'none',
                      transition: 'background 0.4s ease, width 0.3s ease, height 0.3s ease',
                    }}
                  />
                );
              })}
            </div>

            {/* Stage labels under track */}
            <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:342:12" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="342" data-component-end-line="367" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20w-full%22%7D" className="flex justify-between w-full">
              {zones.map((zone, i) => {
                const isPast = i < activeZone;
                const isActive = i === activeZone;
                return (
                  <span data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:347:18" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="347" data-component-end-line="364" data-component-file="ScrollSequenceScene.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-sans%20uppercase%22%7D"
                    key={zone.phase}
                    className="font-sans uppercase"
                    style={{
                      fontSize: '0.52rem',
                      letterSpacing: '0.18em',
                      color: isActive
                        ? 'rgba(26,107,107,0.85)'
                        : isPast
                        ? 'rgba(36,44,44,0.35)'
                        : 'rgba(36,44,44,0.18)',
                      transition: 'color 0.4s ease',
                      textAlign: i === 0 ? 'left' : i === 2 ? 'right' : 'center',
                      flex: 1,
                    }}
                  >
                    {zone.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Zone content — centered, constrained */}
        <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:372:8" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="372" data-component-end-line="425" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20flex%20justify-center%22%7D"
          className="relative w-full flex justify-center"
          style={{ minHeight: 'clamp(11rem, 20vw, 15rem)' }}
        >
          {zones?.map((zone, i) => (
            <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:377:12" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="377" data-component-end-line="423" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20text-center%22%7D"
              key={zone?.phase}
              className="absolute text-center"
              style={{
                width: 'min(90vw, 560px)',
                opacity: activeZone === i ? 1 : 0,
                transform: activeZone === i
                  ? 'translateY(0)'
                  : activeZone > i
                  ? 'translateY(-10px)'
                  : 'translateY(14px)',
                transition: 'opacity 0.55s cubic-bezier(0.4,0,0.2,1), transform 0.55s cubic-bezier(0.4,0,0.2,1)',
                pointerEvents: activeZone === i ? 'auto' : 'none',
              }}
            >
              <p data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:392:14" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="392" data-component-end-line="402" data-component-file="ScrollSequenceScene.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-serif%20mb-4%22%7D"
                className="font-serif mb-4"
                style={{
                  fontSize: 'clamp(1.15rem, 2.4vw, 1.7rem)',
                  color: 'rgba(26,107,107,0.7)',
                  letterSpacing: '0.04em',
                  lineHeight: 1,
                }}
              >
                {zone?.label}
              </p>
              <h2 data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:403:14" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="403" data-component-end-line="412" data-component-file="ScrollSequenceScene.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-serif%20text-balance%20mb-6%22%7D"
                className="font-serif text-balance mb-6"
                style={{
                  fontSize: 'clamp(1.6rem, 3.2vw, 2.6rem)',
                  color: 'rgba(36,44,44,0.88)',
                  lineHeight: 1.08,
                }}
              >
                {zone?.headline}
              </h2>
              <p data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:413:14" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="413" data-component-end-line="422" data-component-file="ScrollSequenceScene.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-sans%20font-light%20leading-relaxed%20mx-auto%22%7D"
                className="font-sans font-light leading-relaxed mx-auto"
                style={{
                  fontSize: 'clamp(0.82rem, 1.1vw, 0.94rem)',
                  color: 'rgba(36,44,44,0.45)',
                  maxWidth: '42ch',
                }}
              >
                {zone?.body}
              </p>
            </div>
          ))}
        </div>

        {/* Stage indicators — bottom */}
        <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:428:8" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="428" data-component-end-line="467" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-10%20left-0%20right-0%20flex%20items-end%20justify-center%22%7D"
          className="absolute bottom-10 left-0 right-0 flex items-end justify-center"
          style={{ gap: 'clamp(2.5rem, 6vw, 5rem)' }}
        >
          {zones?.map((zone, i) => {
            const isActive = i === activeZone;
            const isPast = i < activeZone;
            return (
              <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:436:14" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="436" data-component-end-line="464" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20gap-2%22%7D" key={zone?.phase} className="flex flex-col items-center gap-2">
                <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:437:16" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="437" data-component-end-line="448" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                  style={{
                    width: isActive ? '2rem' : '1rem',
                    height: '1px',
                    background: isActive
                      ? 'rgba(26,107,107,0.7)'
                      : isPast
                      ? 'rgba(26,107,107,0.3)'
                      : 'rgba(36,44,44,0.12)',
                    transition: 'width 0.4s ease, background 0.4s ease',
                  }}
                />
                <span data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:449:16" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="449" data-component-end-line="463" data-component-file="ScrollSequenceScene.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-sans%20uppercase%22%7D"
                  className="font-sans uppercase"
                  style={{
                    fontSize: '0.6rem',
                    letterSpacing: '0.18em',
                    color: isActive
                      ? 'rgba(26,107,107,0.8)'
                      : isPast
                      ? 'rgba(36,44,44,0.3)'
                      : 'rgba(36,44,44,0.15)',
                    transition: 'color 0.4s ease',
                  }}
                >
                  {zone?.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Scroll hint — shown only at start */}
        <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:470:8" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="470" data-component-end-line="487" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-10%20right-8%20flex%20flex-col%20items-center%20gap-1%22%7D"
          className="absolute bottom-10 right-8 flex flex-col items-center gap-1"
          style={{
            opacity: progress < 0.05 ? 0.4 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          <span data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:477:10" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="477" data-component-end-line="479" data-component-file="ScrollSequenceScene.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-sans%20uppercase%20text-%5B0.55rem%5D%20tracking-%5B0.2em%5D%22%2C%22textContent%22%3A%22scroll%22%7D" className="font-sans uppercase text-[0.55rem] tracking-[0.2em]" style={{ color: 'rgba(36,44,44,0.45)' }}>
            scroll
          </span>
          <div data-component-id="src/app/homepage/components/ScrollSequenceScene.tsx:480:10" data-component-path="src/app/homepage/components/ScrollSequenceScene.tsx" data-component-line="480" data-component-end-line="486" data-component-file="ScrollSequenceScene.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            style={{
              width: '1px',
              height: '2rem',
              background: 'linear-gradient(to bottom, rgba(26,107,107,0.4), transparent)',
            }}
          />
        </div>
      </div>
    </div>
  );
}
